import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Shield,
  Search,
  Filter,
  Download,
  Clock,
  User,
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import ExportToExcelButton from "../components/ExportToExcelButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [actions, setActions] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [actionFilter, setActionFilter] = useState("all");
  const [resourceFilter, setResourceFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchData();
    fetchActions();
  }, [actionFilter, resourceFilter, startDate, endDate, offset]);

  const fetchData = async () => {
    const token = localStorage.getItem("token");
    try {
      let url = `${API}/api/security/audit-logs?limit=${limit}&offset=${offset}`;
      if (actionFilter && actionFilter !== "all") url += `&action=${actionFilter}`;
      if (resourceFilter && resourceFilter !== "all") url += `&resource_type=${resourceFilter}`;
      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;
      
      const [logsRes, summaryRes] = await Promise.all([
        fetch(url, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/security/audit-logs/summary?days=30`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      if (logsRes.ok) {
        const data = await logsRes.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
    } catch (err) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchActions = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/api/security/audit-logs/actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActions(data.actions || []);
        setResourceTypes(data.resource_types || []);
      }
    } catch (err) {
      console.error("Failed to fetch action types");
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      create: "bg-green-500/20 text-green-400",
      read: "bg-blue-500/20 text-blue-400",
      update: "bg-yellow-500/20 text-yellow-400",
      delete: "bg-red-500/20 text-red-400",
      login: "bg-cyan-500/20 text-cyan-400",
      logout: "bg-gray-500/20 text-gray-400",
      login_failed: "bg-red-500/20 text-red-400",
      export: "bg-purple-500/20 text-purple-400",
      payment: "bg-green-500/20 text-green-400",
    };
    return <Badge className={colors[action] || "bg-gray-500/20 text-gray-400"}>{action}</Badge>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "blocked":
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const filteredLogs = logs.filter(log => 
    !searchTerm || 
    log.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.resource_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary" />
            Audit Logs
          </h1>
          <p className="text-muted-foreground">Security audit trail and activity monitoring</p>
        </div>
        <ExportToExcelButton path="audit-log" label="Export (Excel)" filename="audit_log.xlsx" />
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-lg border p-4">
            <div className="text-sm text-muted-foreground">Total Events (30d)</div>
            <div className="text-2xl font-bold">{summary.total_events}</div>
          </div>
          <div className="bg-card rounded-lg border p-4">
            <div className="text-sm text-muted-foreground">Unique Users</div>
            <div className="text-2xl font-bold">{summary.unique_users}</div>
          </div>
          <div className="bg-card rounded-lg border p-4">
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <XCircle className="w-3 h-3 text-red-500" /> Failed Actions
            </div>
            <div className="text-2xl font-bold text-red-500">{summary.failed_actions}</div>
          </div>
          <div className="bg-card rounded-lg border p-4">
            <div className="text-sm text-muted-foreground">Most Common</div>
            <div className="text-lg font-semibold">
              {summary.by_action && Object.entries(summary.by_action)
                .sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A"}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by email or resource ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {actions.map(a => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={resourceFilter} onValueChange={setResourceFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Resources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Resources</SelectItem>
            {resourceTypes.map(r => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="w-[150px]"
          placeholder="Start Date"
        />
        <Input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="w-[150px]"
          placeholder="End Date"
        />
      </div>

      {/* Logs Table */}
      <div className="bg-card rounded-lg border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 text-sm font-medium">Time</th>
              <th className="text-left p-3 text-sm font-medium">User</th>
              <th className="text-left p-3 text-sm font-medium">Action</th>
              <th className="text-left p-3 text-sm font-medium">Resource</th>
              <th className="text-left p-3 text-sm font-medium">Status</th>
              <th className="text-left p-3 text-sm font-medium">IP Address</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-muted-foreground">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No audit logs found</p>
                  <p className="text-sm">Activity will be logged as users interact with the system</p>
                </td>
              </tr>
            ) : (
              filteredLogs.map((log) => (
                <tr key={log.id} className="border-t hover:bg-muted/30">
                  <td className="p-3 text-sm">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-muted-foreground" />
                      {formatDate(log.created_at)}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{log.user_email || "System"}</span>
                    </div>
                  </td>
                  <td className="p-3">{getActionBadge(log.action)}</td>
                  <td className="p-3 text-sm">
                    {log.resource_type && (
                      <span className="text-muted-foreground">
                        {log.resource_type}
                        {log.resource_id && <span className="text-xs ml-1">#{log.resource_id.slice(0, 8)}</span>}
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      {getStatusIcon(log.status)}
                      <span className="text-xs capitalize">{log.status}</span>
                    </div>
                  </td>
                  <td className="p-3 text-sm text-muted-foreground">{log.ip_address || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">
            Showing {offset + 1}-{Math.min(offset + limit, total)} of {total} logs
          </p>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
            >
              Previous
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
